import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import EventForm from '../components/forms/EventForm/EventForm';
import AssignmentForm from '../components/forms/AssignmentForm/AssignmentForm';
import { AssignmentApi } from '../services/api/AssignmentApi';
import './CourseScheduleEditor.css';

/**
 * CourseScheduleEditor — embedded inside CourseEdit.
 *
 * Provides a single intuitive surface for setting up an entire course:
 *   • Schedule tab: list / add / edit / delete events (including recurring class meetings)
 *   • Assignments tab: list / add / edit / delete homework, tests, deliverables
 *   • "Quick add weekly class" wizard for the most common case
 *
 * Reuses the canonical EventForm / AssignmentForm components in a modal overlay so
 * we never duplicate validation or API logic.
 */

const TYPE_ICON = {
  lecture: '🎓', lab: '🔬', discussion: '💬', exam: '📝', quiz: '❓',
  assignment: '📋', office_hours: '🏢', meeting: '👥', workshop: '🔧',
  presentation: '📊', field_trip: '🚌', guest_speaker: '🎤', other: '📅',
  personal: '🙋', social: '🎉', work: '💼', appointment: '🩺', reminder: '🔔'
};

const PRIORITY_LABEL = { 0: 'None', 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' };
const PRIORITY_CLASS = { 0: 'p-none', 1: 'p-low', 2: 'p-med', 3: 'p-high', 4: 'p-crit' };

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatEventWhen = (e) => {
  const raw = e.eventDatetime || e.startDatetime || e.startTime || e.start;
  if (!raw) return 'No date set';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return 'Invalid date';
    return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(raw); }
};

const formatDue = (a) => {
  const raw = a.dueDatetime || a.dueDate;
  if (!raw) return 'No due date';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return 'Invalid date';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return String(raw); }
};

const eventDateValue = (e) => {
  const raw = e.eventDatetime || e.startDatetime || e.startTime || e.start || e.endTime;
  const d = raw ? new Date(raw) : null;
  return d && !isNaN(d.getTime()) ? d.getTime() : Infinity;
};

const assignmentDueValue = (a) => {
  const raw = a.dueDatetime || a.dueDate;
  const d = raw ? new Date(raw) : null;
  return d && !isNaN(d.getTime()) ? d.getTime() : Infinity;
};

/* ─────────────────────────────────────────────────────────────────── */
/* Quick "Add weekly class" wizard                                     */
/* ─────────────────────────────────────────────────────────────────── */
function QuickClassWizard({ endeavorId, defaultStartDate, defaultEndDate, onClose, onCreated }) {
  const { createEvent } = useData();
  const [form, setForm] = useState({
    name: '',
    location: '',
    days: [], // 0-6
    startTime: '09:00',
    endTime: '10:00',
    seriesStart: defaultStartDate || new Date().toISOString().slice(0, 10),
    seriesEnd: defaultEndDate || '',
    eventType: 'lecture'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const toggleDay = (d) => setForm(prev => ({
    ...prev,
    days: prev.days.includes(d) ? prev.days.filter(x => x !== d) : [...prev.days, d].sort()
  }));

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (!form.name.trim()) { setError('Please give your class a name.'); return; }
    if (form.days.length === 0) { setError('Pick at least one day of the week.'); return; }
    if (!form.seriesStart || !form.seriesEnd) { setError('Please set both a start and end date for the series.'); return; }
    if (form.endTime <= form.startTime) { setError('End time must be after start time.'); return; }

    setSubmitting(true);
    try {
      // Build payload aligned with EventForm's createEvent path
      const payload = {
        name: form.name.trim(),
        description: '',
        eventDate: form.seriesStart,
        startTime: form.startTime,
        endTime: form.endTime,
        eventType: form.eventType,
        endeavorId,
        location: form.location.trim(),
        isRecurring: true,
        recurringPattern: 'weekly',
        recurringEndDate: form.seriesEnd,
        recurringDaysOfWeek: form.days
      };
      const result = await createEvent(payload);
      if (result && result.error) throw new Error(result.error);
      onCreated?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to create class schedule');
    } finally {
      setSubmitting(false);
    }
  }, [form, endeavorId, createEvent, onCreated, onClose]);

  return (
    <div className="cse-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cse-modal cse-wizard" onClick={e => e.stopPropagation()}>
        <header className="cse-modal-header">
          <h3>📅 Quick add: Weekly class meeting</h3>
          <button className="cse-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cse-modal-body">
          <p className="cse-wizard-hint">
            Create the entire semester's recurring meetings in one step. We'll generate one event per
            scheduled day between the start and end date.
          </p>

          <label className="cse-field">
            <span>Class name</span>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. CS 101 Lecture"
              autoFocus
            />
          </label>

          <div className="cse-row-2">
            <label className="cse-field">
              <span>Type</span>
              <select value={form.eventType} onChange={e => setForm({ ...form, eventType: e.target.value })}>
                <option value="lecture">🎓 Lecture</option>
                <option value="lab">🔬 Lab</option>
                <option value="discussion">💬 Discussion</option>
                <option value="workshop">🔧 Workshop</option>
                <option value="office_hours">🏢 Office Hours</option>
                <option value="meeting">👥 Meeting</option>
              </select>
            </label>
            <label className="cse-field">
              <span>Location (optional)</span>
              <input
                type="text"
                value={form.location}
                onChange={e => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Room 204 / Zoom"
              />
            </label>
          </div>

          <div className="cse-field">
            <span>Meeting days</span>
            <div className="cse-day-picker" role="group" aria-label="Days of week">
              {DAY_LABELS.map((label, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`cse-day-chip ${form.days.includes(idx) ? 'active' : ''}`}
                  onClick={() => toggleDay(idx)}
                  aria-pressed={form.days.includes(idx)}
                >{label}</button>
              ))}
            </div>
          </div>

          <div className="cse-row-2">
            <label className="cse-field">
              <span>Start time</span>
              <input type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
            </label>
            <label className="cse-field">
              <span>End time</span>
              <input type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
            </label>
          </div>

          <div className="cse-row-2">
            <label className="cse-field">
              <span>Series start date</span>
              <input type="date" value={form.seriesStart} onChange={e => setForm({ ...form, seriesStart: e.target.value })} />
            </label>
            <label className="cse-field">
              <span>Series end date</span>
              <input type="date" value={form.seriesEnd} onChange={e => setForm({ ...form, seriesEnd: e.target.value })} />
            </label>
          </div>

          {error && <div className="cse-error">{error}</div>}
        </div>
        <footer className="cse-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating series…' : 'Create class schedule'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Modal wrappers around the existing forms                            */
/* ─────────────────────────────────────────────────────────────────── */
function EventFormModal({ endeavorId, eventId, defaultDate, onClose, onSaved }) {
  return (
    <div className="cse-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cse-modal cse-modal-wide" onClick={e => e.stopPropagation()}>
        <header className="cse-modal-header">
          <h3>{eventId ? '✏️ Edit event' : '✨ New event'}</h3>
          <button className="cse-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cse-modal-body cse-form-body">
          <EventForm
            isEditMode={Boolean(eventId)}
            eventId={eventId}
            prefill={{ endeavorId, selectedDate: defaultDate }}
            onCancel={onClose}
            onSuccess={() => { onSaved?.(); onClose?.(); }}
            compact
          />
        </div>
      </div>
    </div>
  );
}

function AssignmentFormModal({ endeavorId, assignment, defaultEventId, onClose, onSaved }) {
  // AssignmentForm only does create. For edit we render an inline editor (simpler fields).
  if (assignment) {
    return <AssignmentEditModal endeavorId={endeavorId} assignment={assignment} onClose={onClose} onSaved={onSaved} />;
  }
  return (
    <div className="cse-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cse-modal cse-modal-wide" onClick={e => e.stopPropagation()}>
        <header className="cse-modal-header">
          <h3>✨ New assignment</h3>
          <button className="cse-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cse-modal-body cse-form-body">
          <AssignmentForm
            prefill={{ endeavorId, eventId: defaultEventId }}
            onCancel={onClose}
            onSuccess={() => { onSaved?.(); onClose?.(); }}
            compact
          />
        </div>
      </div>
    </div>
  );
}

/* Inline assignment editor (since AssignmentForm doesn't have an edit mode) */
function AssignmentEditModal({ endeavorId, assignment, onClose, onSaved }) {
  const { updateAssignment } = useData();
  const toLocalDT = (raw) => {
    if (!raw) return '';
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  const [form, setForm] = useState({
    title: assignment.title || assignment.name || '',
    description: assignment.description || '',
    dueDatetime: toLocalDT(assignment.dueDatetime || assignment.dueDate),
    priority: assignment.priority ?? 2,
    eventId: assignment.eventId || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        priority: Number(form.priority),
        dueDatetime: form.dueDatetime ? new Date(form.dueDatetime).toISOString() : null,
        eventId: form.eventId ? Number(form.eventId) : null,
        endeavorId
      };
      const res = await updateAssignment(assignment.assignmentId || assignment.id, payload);
      if (res && res.ok === false) throw new Error('Update failed');
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to update assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cse-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cse-modal" onClick={e => e.stopPropagation()}>
        <header className="cse-modal-header">
          <h3>✏️ Edit assignment</h3>
          <button className="cse-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cse-modal-body">
          <label className="cse-field">
            <span>Title</span>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
          </label>
          <label className="cse-field">
            <span>Description</span>
            <textarea rows="4" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </label>
          <div className="cse-row-2">
            <label className="cse-field">
              <span>Due</span>
              <input type="datetime-local" value={form.dueDatetime} onChange={e => setForm({ ...form, dueDatetime: e.target.value })} />
            </label>
            <label className="cse-field">
              <span>Priority</span>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value={0}>None</option>
                <option value={1}>Low</option>
                <option value={2}>Medium</option>
                <option value={3}>High</option>
                <option value={4}>Critical</option>
              </select>
            </label>
          </div>
          {error && <div className="cse-error">{error}</div>}
        </div>
        <footer className="cse-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </footer>
      </div>
    </div>
  );
}

/* Simple confirm dialog used for destructive actions */
function ConfirmDialog({ title, message, confirmLabel = 'Delete', danger = true, onConfirm, onCancel, choices }) {
  return (
    <div className="cse-modal-overlay" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="cse-modal cse-modal-small" onClick={e => e.stopPropagation()}>
        <header className="cse-modal-header">
          <h3>{title}</h3>
          <button className="cse-icon-btn" onClick={onCancel} aria-label="Close">✕</button>
        </header>
        <div className="cse-modal-body">
          <p>{message}</p>
        </div>
        <footer className="cse-modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          {choices ? choices.map(c => (
            <button
              key={c.value}
              className={`btn ${c.danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => onConfirm(c.value)}
            >{c.label}</button>
          )) : (
            <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => onConfirm(true)}>{confirmLabel}</button>
          )}
        </footer>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Main component                                                      */
/* ─────────────────────────────────────────────────────────────────── */
export default function CourseScheduleEditor({ courseId, startDate, endDate }) {
  const { getEndeavorEvents, getEndeavorAssignments, deleteEvent, deleteEventSeries, clearCache } = useData();
  const [tab, setTab] = useState('schedule');
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Modal state
  const [eventModal, setEventModal] = useState(null);   // { eventId? }
  const [wizardOpen, setWizardOpen] = useState(false);
  const [assignmentModal, setAssignmentModal] = useState(null); // { assignment?, defaultEventId? }
  const [confirm, setConfirm] = useState(null);         // { type, ...payload }

  // Filters
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // all | event | standalone | overdue | completed

  const refreshEvents = useCallback(async () => {
    if (!courseId) return;
    setLoadingEvents(true);
    try {
      clearCache(`endeavor-events_${courseId}`);
      clearCache('index_events');
      const data = await getEndeavorEvents(courseId);
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load events', e);
    } finally {
      setLoadingEvents(false);
    }
  }, [courseId, getEndeavorEvents, clearCache]);

  const refreshAssignments = useCallback(async () => {
    if (!courseId) return;
    setLoadingAssignments(true);
    try {
      clearCache(`endeavor-assignments_${courseId}`);
      clearCache('index_assignments');
      const data = await getEndeavorAssignments(courseId);
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load assignments', e);
    } finally {
      setLoadingAssignments(false);
    }
  }, [courseId, getEndeavorAssignments, clearCache]);

  useEffect(() => { refreshEvents(); refreshAssignments(); }, [refreshEvents, refreshAssignments]);

  /* Event grouping: parent recurring series (with children) vs one-offs */
  const grouped = useMemo(() => {
    const series = new Map();   // parentId -> { parent, children: [] }
    const oneOffs = [];
    const parents = events.filter(e => e.isRecurring && !e.parentEventId);
    parents.forEach(p => series.set(p.eventId || p.id, { parent: p, children: [] }));
    events.forEach(e => {
      const eid = e.eventId || e.id;
      const pid = e.parentEventId;
      if (pid && series.has(pid)) series.get(pid).children.push(e);
      else if (!e.isRecurring && !pid) oneOffs.push(e);
      else if (!series.has(eid) && !pid) oneOffs.push(e);
    });
    const seriesArr = [...series.values()].sort((a, b) => eventDateValue(a.parent) - eventDateValue(b.parent));
    seriesArr.forEach(s => s.children.sort((a, b) => eventDateValue(a) - eventDateValue(b)));
    oneOffs.sort((a, b) => eventDateValue(a) - eventDateValue(b));
    return { series: seriesArr, oneOffs };
  }, [events]);

  const filteredAssignments = useMemo(() => {
    const now = Date.now();
    let list = [...assignments];
    if (assignmentFilter === 'event') list = list.filter(a => a.eventId);
    else if (assignmentFilter === 'standalone') list = list.filter(a => !a.eventId);
    else if (assignmentFilter === 'overdue') list = list.filter(a => !a.completed && assignmentDueValue(a) < now);
    else if (assignmentFilter === 'completed') list = list.filter(a => a.completed);
    list.sort((a, b) => assignmentDueValue(a) - assignmentDueValue(b));
    return list;
  }, [assignments, assignmentFilter]);

  const assignmentsByEventId = useMemo(() => {
    const map = new Map();
    assignments.forEach(a => {
      if (a.eventId) {
        const list = map.get(a.eventId) || [];
        list.push(a);
        map.set(a.eventId, list);
      }
    });
    return map;
  }, [assignments]);

  /* Delete handlers */
  const handleDeleteEvent = useCallback(async (mode) => {
    const evt = confirm?.event;
    if (!evt) return;
    const eid = evt.eventId || evt.id;
    try {
      if (mode === 'series') {
        const parentId = evt.parentEventId || eid;
        await deleteEventSeries(parentId);
      } else {
        await deleteEvent(eid);
      }
      await refreshEvents();
    } catch (e) {
      console.error('Failed to delete event', e);
    } finally {
      setConfirm(null);
    }
  }, [confirm, deleteEvent, deleteEventSeries, refreshEvents]);

  const handleDeleteAssignment = useCallback(async () => {
    const asn = confirm?.assignment;
    if (!asn) return;
    try {
      const res = await AssignmentApi.deleteAssignment(asn.assignmentId || asn.id);
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      clearCache(`endeavor-assignments_${courseId}`);
      clearCache('index_assignments');
      await refreshAssignments();
    } catch (e) {
      console.error('Failed to delete assignment', e);
    } finally {
      setConfirm(null);
    }
  }, [confirm, courseId, refreshAssignments, clearCache]);

  const handleToggleAssignmentComplete = useCallback(async (asn) => {
    try {
      if (asn.completed) {
        // Mark incomplete via update
        await AssignmentApi.updateAssignment(asn.assignmentId || asn.id, { completed: false, completedDatetime: null });
      } else {
        await AssignmentApi.markAssignmentCompleted(asn.assignmentId || asn.id);
      }
      clearCache(`endeavor-assignments_${courseId}`);
      await refreshAssignments();
    } catch (e) { console.error(e); }
  }, [courseId, refreshAssignments, clearCache]);

  /* Render */
  return (
    <div className="course-schedule-editor">
      <div className="cse-tabs" role="tablist">
        <button
          role="tab"
          className={`cse-tab ${tab === 'schedule' ? 'active' : ''}`}
          aria-selected={tab === 'schedule'}
          onClick={() => setTab('schedule')}
        >📅 Schedule <span className="cse-tab-count">{events.length}</span></button>
        <button
          role="tab"
          className={`cse-tab ${tab === 'assignments' ? 'active' : ''}`}
          aria-selected={tab === 'assignments'}
          onClick={() => setTab('assignments')}
        >📋 Assignments <span className="cse-tab-count">{assignments.length}</span></button>
      </div>

      {tab === 'schedule' && (
        <div className="cse-panel">
          <div className="cse-panel-header">
            <div className="cse-panel-title">
              <h3>Course Schedule</h3>
              <p className="cse-hint">Class meetings, exams, office hours, and one-off events.</p>
            </div>
            <div className="cse-panel-actions">
              <button className="btn btn-primary" onClick={() => setWizardOpen(true)}>
                📅 Add weekly class
              </button>
              <button className="btn btn-secondary" onClick={() => setEventModal({})}>
                + Single event
              </button>
            </div>
          </div>

          {loadingEvents && <div className="cse-loading">Loading schedule…</div>}

          {!loadingEvents && events.length === 0 && (
            <div className="cse-empty">
              <div className="cse-empty-icon">📅</div>
              <h4>No events yet</h4>
              <p>Set up your weekly class schedule in one step, or add individual events.</p>
              <button className="btn btn-primary" onClick={() => setWizardOpen(true)}>Add weekly class</button>
            </div>
          )}

          {!loadingEvents && grouped.series.map(({ parent, children }) => {
            const pid = parent.eventId || parent.id;
            return (
              <div key={`s-${pid}`} className="cse-series">
                <div className="cse-series-header">
                  <div className="cse-series-title">
                    <span className="cse-type-icon">{TYPE_ICON[parent.eventTypeName || 'lecture'] || '📅'}</span>
                    <strong>{parent.name || 'Untitled class'}</strong>
                    <span className="cse-badge cse-badge-info">{children.length || 0} sessions</span>
                    {parent.location && <span className="cse-meta">📍 {parent.location}</span>}
                  </div>
                  <div className="cse-row-actions">
                    <button className="cse-link-btn" onClick={() => setEventModal({ eventId: pid })}>Edit series</button>
                    <button className="cse-link-btn danger" onClick={() => setConfirm({ type: 'event', event: parent, isSeries: true })}>Delete series</button>
                  </div>
                </div>
                {children.length > 0 && (
                  <ul className="cse-series-children">
                    {children.slice(0, 6).map(c => {
                      const cid = c.eventId || c.id;
                      return (
                        <li key={cid} className="cse-event-row compact">
                          <span className="cse-when">{formatEventWhen(c)}</span>
                          <div className="cse-row-actions">
                            <button className="cse-link-btn" onClick={() => setEventModal({ eventId: cid })}>Edit</button>
                            <button className="cse-link-btn danger" onClick={() => setConfirm({ type: 'event', event: c })}>Delete</button>
                          </div>
                        </li>
                      );
                    })}
                    {children.length > 6 && <li className="cse-more">+{children.length - 6} more sessions</li>}
                  </ul>
                )}
              </div>
            );
          })}

          {!loadingEvents && grouped.oneOffs.length > 0 && (
            <div className="cse-section">
              <h4 className="cse-section-title">One-off events</h4>
              <ul className="cse-list">
                {grouped.oneOffs.map(e => {
                  const eid = e.eventId || e.id;
                  const attached = assignmentsByEventId.get(eid)?.length || 0;
                  return (
                    <li key={eid} className="cse-event-row">
                      <div className="cse-event-main">
                        <span className="cse-type-icon">{TYPE_ICON[e.eventTypeName] || '📅'}</span>
                        <div className="cse-event-text">
                          <div className="cse-event-title">{e.name || 'Untitled event'}</div>
                          <div className="cse-event-sub">
                            {formatEventWhen(e)}
                            {e.location && <> • 📍 {e.location}</>}
                            {attached > 0 && <> • 📎 {attached} assignment{attached > 1 ? 's' : ''}</>}
                          </div>
                        </div>
                      </div>
                      <div className="cse-row-actions">
                        <button className="cse-link-btn" onClick={() => setAssignmentModal({ defaultEventId: eid })}>+ Assignment</button>
                        <button className="cse-link-btn" onClick={() => setEventModal({ eventId: eid })}>Edit</button>
                        <button className="cse-link-btn danger" onClick={() => setConfirm({ type: 'event', event: e })}>Delete</button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'assignments' && (
        <div className="cse-panel">
          <div className="cse-panel-header">
            <div className="cse-panel-title">
              <h3>Assignments</h3>
              <p className="cse-hint">Homework, tests, projects, and other deliverables.</p>
            </div>
            <div className="cse-panel-actions">
              <button className="btn btn-primary" onClick={() => setAssignmentModal({})}>+ New assignment</button>
            </div>
          </div>

          <div className="cse-filter-chips" role="tablist">
            {[
              { value: 'all', label: `All (${assignments.length})` },
              { value: 'event', label: 'Attached to event' },
              { value: 'standalone', label: 'Standalone' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'completed', label: 'Completed' }
            ].map(f => (
              <button
                key={f.value}
                className={`cse-chip ${assignmentFilter === f.value ? 'active' : ''}`}
                onClick={() => setAssignmentFilter(f.value)}
              >{f.label}</button>
            ))}
          </div>

          {loadingAssignments && <div className="cse-loading">Loading assignments…</div>}

          {!loadingAssignments && filteredAssignments.length === 0 && (
            <div className="cse-empty">
              <div className="cse-empty-icon">📋</div>
              <h4>No assignments {assignmentFilter !== 'all' ? 'match this filter' : 'yet'}</h4>
              {assignmentFilter === 'all' && (
                <button className="btn btn-primary" onClick={() => setAssignmentModal({})}>Add first assignment</button>
              )}
            </div>
          )}

          {!loadingAssignments && filteredAssignments.length > 0 && (
            <ul className="cse-list">
              {filteredAssignments.map(a => {
                const aid = a.assignmentId || a.id;
                const overdue = !a.completed && assignmentDueValue(a) < Date.now();
                const linkedEvent = a.eventId ? events.find(e => (e.eventId || e.id) === a.eventId) : null;
                return (
                  <li key={aid} className={`cse-assignment-row ${a.completed ? 'is-completed' : ''} ${overdue ? 'is-overdue' : ''}`}>
                    <label className="cse-check">
                      <input
                        type="checkbox"
                        checked={!!a.completed}
                        onChange={() => handleToggleAssignmentComplete(a)}
                        aria-label={a.completed ? 'Mark incomplete' : 'Mark complete'}
                      />
                    </label>
                    <div className="cse-assignment-main">
                      <div className="cse-assignment-title">
                        {a.title || a.name || 'Untitled assignment'}
                        <span className={`cse-priority ${PRIORITY_CLASS[a.priority ?? 2]}`}>{PRIORITY_LABEL[a.priority ?? 2]}</span>
                        {overdue && <span className="cse-badge cse-badge-danger">Overdue</span>}
                      </div>
                      <div className="cse-assignment-sub">
                        Due {formatDue(a)}
                        {linkedEvent && <> • 📎 {linkedEvent.name}</>}
                      </div>
                    </div>
                    <div className="cse-row-actions">
                      <button className="cse-link-btn" onClick={() => setAssignmentModal({ assignment: a })}>Edit</button>
                      <button className="cse-link-btn danger" onClick={() => setConfirm({ type: 'assignment', assignment: a })}>Delete</button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Modals */}
      {wizardOpen && (
        <QuickClassWizard
          endeavorId={courseId}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          onClose={() => setWizardOpen(false)}
          onCreated={refreshEvents}
        />
      )}

      {eventModal && (
        <EventFormModal
          endeavorId={courseId}
          eventId={eventModal.eventId}
          defaultDate={startDate}
          onClose={() => setEventModal(null)}
          onSaved={refreshEvents}
        />
      )}

      {assignmentModal && (
        <AssignmentFormModal
          endeavorId={courseId}
          assignment={assignmentModal.assignment}
          defaultEventId={assignmentModal.defaultEventId}
          onClose={() => setAssignmentModal(null)}
          onSaved={refreshAssignments}
        />
      )}

      {confirm?.type === 'event' && (
        <ConfirmDialog
          title="Delete event"
          message={
            confirm.isSeries
              ? `Delete the entire "${confirm.event.name}" series? This removes the parent event and all sessions.`
              : confirm.event.parentEventId
                ? `Delete this session of "${confirm.event.name}"? You can also delete the entire series.`
                : `Delete "${confirm.event.name || 'this event'}"? This cannot be undone.`
          }
          onCancel={() => setConfirm(null)}
          onConfirm={handleDeleteEvent}
          choices={
            confirm.event.parentEventId
              ? [
                  { value: 'one', label: 'This event only', danger: true },
                  { value: 'series', label: 'Entire series', danger: true }
                ]
              : confirm.isSeries
                ? [{ value: 'series', label: 'Delete series', danger: true }]
                : [{ value: 'one', label: 'Delete event', danger: true }]
          }
        />
      )}

      {confirm?.type === 'assignment' && (
        <ConfirmDialog
          title="Delete assignment"
          message={`Delete "${confirm.assignment.title || confirm.assignment.name || 'this assignment'}"? This cannot be undone.`}
          onCancel={() => setConfirm(null)}
          onConfirm={handleDeleteAssignment}
        />
      )}
    </div>
  );
}
